import { headset } from "./headsetVerbindung";
import { softphone } from "./linkusSdk";
import { soundSystem } from "./soundSystem";

/**
 * Welche Geräte die Telefonie benutzt — Mikrofon, Lautsprecher fürs Gespräch
 * und Lautsprecher für den Klingelton (08.10.2026 Klingelton, 12.10.2026
 * „Einstellung für Telefon … Gerät aussuchen für Mikro und Lautsprecher").
 *
 * Jede der drei Einstellungen steht auf „Automatisch" oder fest auf einem
 * Gerät:
 *  - Automatisch: Ist ein Headset angeschlossen (Name aus WebHID, sonst ein
 *    Gerät mit „Jabra" im Namen), dann das Headset; sonst der Standard des
 *    Rechners.
 *  - Fest gewählt: dieses Gerät. Ist es nicht (mehr) angeschlossen, gilt
 *    wieder „Automatisch".
 *
 * Vorher lief das Gespräch immer über das Standardgerät von Windows — steckt
 * das Jabra, ist aber der Laptop-Lautsprecher Standard, hörte man den
 * Anrufer nicht im Headset (Telefonie-Debug 10.10.2026).
 *
 * Gilt je Browser (localStorage): die Geräte sind die des Rechners.
 */

export const AUTOMATISCH = "auto";
/** Alter Name aus der Klingelton-Einstellung, bleibt für die Tests. */
export const KLINGEL_AUTOMATISCH = AUTOMATISCH;

export type GeraeteRolle = "mikrofon" | "lautsprecher" | "klingel";

const SPEICHER: Record<GeraeteRolle, string> = {
    mikrofon: "kmvs.telefon.mikrofon",
    lautsprecher: "kmvs.telefon.lautsprecher",
    klingel: "kmvs.klingelGeraet",
};

export interface AudioGeraet {
    deviceId: string;
    label: string;
}
/** Alter Name aus der Klingelton-Einstellung. */
export type AusgabeGeraet = AudioGeraet;

/** Sammel-Einträge des Browsers, die kein eigenes Gerät sind. */
const SAMMEL_IDS = new Set(["default", "communications", ""]);

export const echteGeraete = (geraete: AudioGeraet[]): AudioGeraet[] => geraete.filter((g) => !SAMMEL_IDS.has(g.deviceId));

/**
 * Das zu benutzende Gerät. "" heißt Standard des Systems.
 * `headsetName` = Name des per WebHID verbundenen Headsets (falls eins).
 */
export function geraetWaehlen(einstellung: string, geraete: AudioGeraet[], headsetName: string | null): string {
    if (einstellung !== AUTOMATISCH && geraete.some((g) => g.deviceId === einstellung)) return einstellung;
    const echte = echteGeraete(geraete);
    const name = headsetName?.trim().toLowerCase();
    const treffer =
        (name ? echte.find((g) => g.label.toLowerCase().includes(name)) : undefined) ??
        echte.find((g) => g.label.toLowerCase().includes("jabra"));
    return treffer?.deviceId ?? "";
}
/** Alter Name aus der Klingelton-Einstellung. */
export const klingelSinkWaehlen = geraetWaehlen;

export interface RollenStatus {
    einstellung: string;
    /** Gerät, das gerade benutzt wird ("" = Standard). */
    aktiv: string;
    /** Name für die Anzeige. */
    zielName: string;
}

export interface AudioGeraeteStatus {
    /** Kann der Browser Ausgabegeräte wählen (Edge/Chrome)? */
    ausgabeWaehlbar: boolean;
    /** Kann der Klingelton (Web Audio) ein eigenes Gerät nehmen? */
    klingelWaehlbar: boolean;
    mikrofone: AudioGeraet[];
    lautsprecher: AudioGeraet[];
    /** Ohne Mikrofon-Freigabe liefert der Browser keine Gerätenamen. */
    namenFehlen: boolean;
    rollen: Record<GeraeteRolle, RollenStatus>;
}

function lesen(rolle: GeraeteRolle): string {
    try {
        return window.localStorage.getItem(SPEICHER[rolle]) || AUTOMATISCH;
    } catch {
        return AUTOMATISCH;
    }
}

function schreiben(rolle: GeraeteRolle, wert: string): void {
    try {
        if (wert === AUTOMATISCH) window.localStorage.removeItem(SPEICHER[rolle]);
        else window.localStorage.setItem(SPEICHER[rolle], wert);
    } catch {
        /* privater Modus — gilt dann nur bis zum Neuladen */
    }
}

function ausgabeWaehlbar(): boolean {
    return typeof HTMLMediaElement !== "undefined" && "setSinkId" in HTMLMediaElement.prototype;
}

function klingelWaehlbar(): boolean {
    if (typeof window === "undefined" || !window.AudioContext) return false;
    return typeof (window.AudioContext.prototype as unknown as { setSinkId?: unknown }).setSinkId === "function";
}

const STANDARD_NAME: Record<GeraeteRolle, string> = {
    mikrofon: "Standard-Mikrofon",
    lautsprecher: "Standard-Lautsprecher",
    klingel: "Standard-Lautsprecher",
};

const leererStatus = (rolle: GeraeteRolle): RollenStatus => ({
    einstellung: AUTOMATISCH,
    aktiv: "",
    zielName: STANDARD_NAME[rolle],
});

class AudioGeraete {
    private status: AudioGeraeteStatus = {
        ausgabeWaehlbar: false,
        klingelWaehlbar: false,
        mikrofone: [],
        lautsprecher: [],
        namenFehlen: false,
        rollen: { mikrofon: leererStatus("mikrofon"), lautsprecher: leererStatus("lautsprecher"), klingel: leererStatus("klingel") },
    };
    private readonly zuhoerer = new Set<() => void>();
    private gestartet = false;

    getStatus(): AudioGeraeteStatus {
        return this.status;
    }

    abonnieren(fn: () => void): () => void {
        this.zuhoerer.add(fn);
        return () => this.zuhoerer.delete(fn);
    }

    /** Einmal je Tab: Geräte lesen und bei Änderungen (anstecken, Headset) nachziehen. */
    starten(): void {
        if (this.gestartet || typeof window === "undefined") return;
        this.gestartet = true;
        const r = this.status.rollen;
        this.status = {
            ...this.status,
            ausgabeWaehlbar: ausgabeWaehlbar(),
            klingelWaehlbar: klingelWaehlbar(),
            rollen: {
                mikrofon: { ...r.mikrofon, einstellung: lesen("mikrofon") },
                lautsprecher: { ...r.lautsprecher, einstellung: lesen("lautsprecher") },
                klingel: { ...r.klingel, einstellung: lesen("klingel") },
            },
        };
        navigator.mediaDevices?.addEventListener?.("devicechange", () => void this.aktualisieren());
        headset.abonnieren(() => void this.aktualisieren());
        void this.aktualisieren();
    }

    setzeEinstellung(rolle: GeraeteRolle, wert: string): void {
        schreiben(rolle, wert);
        const r = this.status.rollen;
        this.status = { ...this.status, rollen: { ...r, [rolle]: { ...r[rolle], einstellung: wert } } };
        void this.aktualisieren();
    }

    /**
     * Gerätenamen freischalten: Der Browser nennt Geräte erst, wenn die Seite
     * einmal das Mikrofon benutzen durfte. Muss aus einem Klick kommen.
     */
    async namenFreigeben(): Promise<void> {
        try {
            const s = await navigator.mediaDevices.getUserMedia({ audio: true });
            s.getTracks().forEach((t) => t.stop());
        } catch {
            /* abgelehnt — die Liste bleibt ohne Namen */
        }
        await this.aktualisieren();
    }

    async aktualisieren(): Promise<void> {
        let alle: MediaDeviceInfo[] = [];
        try {
            alle = (await navigator.mediaDevices?.enumerateDevices?.()) ?? [];
        } catch {
            /* ohne Liste bleibt der Standard */
        }
        const liste = (art: MediaDeviceKind, wort: string): AudioGeraet[] =>
            alle
                .filter((d) => d.kind === art)
                .map((d, i) => ({ deviceId: d.deviceId, label: d.label || `${wort} ${i + 1}` }));
        const mikrofone = liste("audioinput", "Mikrofon");
        const lautsprecher = liste("audiooutput", "Lautsprecher");
        const namenFehlen = alle.some((d) => (d.kind === "audioinput" || d.kind === "audiooutput") && !d.label);
        const headsetName = headset.getStatus().geraet;

        const rolle = (r: GeraeteRolle, geraete: AudioGeraet[]): RollenStatus => {
            const einstellung = this.status.rollen[r].einstellung;
            const aktiv = geraetWaehlen(einstellung, geraete, headsetName);
            const ziel = geraete.find((g) => g.deviceId === aktiv);
            return { einstellung, aktiv, zielName: ziel && !SAMMEL_IDS.has(aktiv) ? ziel.label : STANDARD_NAME[r] };
        };
        const rollen = {
            mikrofon: rolle("mikrofon", mikrofone),
            lautsprecher: rolle("lautsprecher", lautsprecher),
            klingel: rolle("klingel", lautsprecher),
        };

        soundSystem.setzeKlingelGeraet(rollen.klingel.aktiv);
        if (this.status.ausgabeWaehlbar) softphone.setzeLautsprecher(rollen.lautsprecher.aktiv);
        void softphone.setzeMikrofon(rollen.mikrofon.aktiv);

        this.status = { ...this.status, mikrofone, lautsprecher, namenFehlen, rollen };
        this.zuhoerer.forEach((fn) => fn());
    }
}

export const audioGeraete = new AudioGeraete();
